// Reglas de formato numérico para dashboards (ver "AJUSTES DE MAGNITUDES NUMERICAS"):
// Regla 1  -> espacio fino separador de miles, sin decimales
// Regla 2  -> espacio fino separador de miles, 2 decimales
// Regla 2+ -> espacio fino separador de miles, 2 decimales (5 si los primeros dos son cero)
// Regla 3  -> 2 decimales + "%"
const THOUSANDS_SEPARATOR = " "; // narrow no-break space

const isValidNumber = (num) => num !== null && num !== undefined && isFinite(num) && !isNaN(num);

// Exportado: otros formateadores del proyecto (ej. GEIPDFReport.js, que
// arma sus propios decimales adaptativos por fila) reusan esto en vez de
// reimplementar el separador — un solo lugar de verdad para el agrupado.
//
// `separator` es parametrizable por un motivo puntual: las fuentes
// estándar de jsPDF (Helvetica/Times/Courier) usan codificación WinAnsi,
// que NO tiene el espacio fino (U+202F) — se renderiza como un glifo
// roto (se vio como "/" en el PDF de GEI). Para exportadores a PDF hay
// que pasar un espacio común (" ", sí soportado) en vez del default.
export const groupThousands = (integerDigits, separator = THOUSANDS_SEPARATOR) =>
  integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

const withThousands = (num, decimals) => {
  const fixed = Math.abs(Number(num)).toFixed(decimals);
  const [intPart, decPart] = fixed.split(".");
  const sign = Number(num) < 0 ? "-" : "";
  const groupedInt = groupThousands(intPart);
  return decPart !== undefined ? `${sign}${groupedInt}.${decPart}` : `${sign}${groupedInt}`;
};

// Regla 1: espacio fino separador de miles, sin decimales
export const formatWhole = (num) => {
  if (!isValidNumber(num)) return "-";
  return withThousands(Math.round(Number(num)), 0);
};

// Regla 2: espacio fino separador de miles, N decimales (por defecto 2)
export const formatMagnitude = (num, decimals = 2) => {
  if (!isValidNumber(num)) return "-";
  return withThousands(num, decimals);
};

// Regla 2+: como Regla 2, pero usa exceptionalDecimals cuando los primeros `decimals` son cero
// (ej. con decimals=2: |num| < 0.01, como 0.0074 -> los primeros dos decimales son "00")
export const formatMagnitudeAdaptive = (num, decimals = 2, exceptionalDecimals = 5) => {
  if (!isValidNumber(num)) return "-";
  const isExceptional = Number(num) !== 0 && Math.abs(Number(num)) < Math.pow(10, -decimals);
  return withThousands(num, isExceptional ? exceptionalDecimals : decimals);
};

// Para ejes de gráficos: los ticks los genera la escala del chart (suelen
// ser números redondos, ej. 0,1,2...6) — aplicarles Regla 2+ a rajatabla
// fuerza ".00" en cada uno ("1.00", "6.00"), se ve como ceros de más que
// nadie pidió. Si el tick es (casi) un entero se muestra sin decimales
// (Regla 1); si tiene parte fraccionaria real, ahí sí Regla 2+ normal.
export const formatMagnitudeAdaptiveAxis = (num, decimals = 2, exceptionalDecimals = 5) => {
  if (!isValidNumber(num)) return "-";
  const n = Number(num);
  if (Math.abs(n - Math.round(n)) < 1e-9) return formatWhole(n);
  return formatMagnitudeAdaptive(n, decimals, exceptionalDecimals);
};

// Regla 3: 2 decimales + " %"
export const formatPercentage = (num, decimals = 2) => {
  if (!isValidNumber(num)) return "-";
  return `${Number(num).toFixed(decimals)} %`;
};
